import { h, Component } from 'preact';
import { configure } from '../../config';
import style from './style';
import { Text, MarkupText } from 'preact-i18n';
import { CcpaButtons } from '../ccpa-buttons';
import { Button } from '@cube/blocks';
import callWith from '@os/call-with';
import SynAff from '@cube/synaff';
import dlv from 'dlv';
import cx from 'classnames';
import { trackEvent, STANDARD_KEYS, INPUT_KEYS } from '@cube/track';


// list of personal cookies
// format of each entry is name, description, value formatter function
// missing value formatter will simply output the value
const PERSONAL_COOKIES = [
	[
		'temporalchip',
		'Unique ID based on base64 encoding of IP address and timestamp. Used as an identifier for a specific session',
		v => ({ rawValue: v, decodedValue: atob(decodeURIComponent(v)) })
	],
	[
		'eternalchip',
		'Unique ID based on base64 encoding of IP address and timestamp. Used as an identifier for a specific visitor across multiple visits',
		v => ({ rawValue: v, decodedValue: atob(decodeURIComponent(v)) })
	],
	[
		'synaff',
		'Bitmap encoding of categories (e.g. sports, entertainment) that a user has interacted with to use for personalization',
		v => ({ rawValue: v, decodedValue: (new SynAff()).readAndLabel() })
	],
	[
		'session',
		'Session ID'
	]
];

function safeJSONParse(v) {
	try {
		return JSON.parse(v);
	}
	catch (e) {
		return v;
	}
}


// list of personal local storage entries
// format of each entry is name, description, value formatter function
// missing value formatter will simply output the value
const PERSONAL_LOCAL_STORAGE = [
	//weather widget and anything else that uses location
	[
		'location-model',
		'Location preferences',
		safeJSONParse
	],
	//finance widget
	[
		'finance-components.stock-recently-viewed',
		'Recently Viewed Stock Quotes',
		safeJSONParse
	],
	[
		'sync-user-preferences',
		'General User preferences',
		safeJSONParse
	],
	[
		'notifications',
		'Information about browser notifications you have seen',
		safeJSONParse
	],
	[
		'alerts.dismissed',
		'Information on browser based alerts you have dismissed',
		safeJSONParse
	],
	[
		'@cube/video-hero.gen4_tvmovies',
		'Information about videos you have been served and/or watched',
		safeJSONParse
	]
];

const DELETING_DATA = 2;
const DELETED_DATA = 3;
const DELETE_FAILED = 4;
const HAS_DATA = 5;

const STATUS_TO_TEXT_ID = [
	'statusChecking',
	'statusNoData',
	'statusDeleting',
	'statusDeleted',
	'statusFailed'
];

const TYPE_COOKIE=1;
const TYPE_LOCALSTORAGE=2;

export class CcpaDataWidget extends Component {

	updateStatus = (name, status) => {
		let userData = { ...this.state.userData };
		userData[name].status = status;
		this.setState({ userData });
	}

	deleteData = (name) => {
		trackEvent( this.context, {
			[INPUT_KEYS.action]: 'click',
			[INPUT_KEYS.priority]: 'high',
			[STANDARD_KEYS.cubecomponent]: 'CcpaData.Delete',
			[STANDARD_KEYS.contenttext]: name
		});

		if (this.state.userData[name].type === TYPE_COOKIE) {
			document.cookie = `${name}=;max-age=0`; //delete the cookie
			this.updateStatus(name, DELETED_DATA);
			return;
		}
		else if (this.state.userData[name].type === TYPE_LOCALSTORAGE) {
			localStorage.removeItem(name);
			this.updateStatus(name, DELETED_DATA);
			return;
		}

		this.updateStatus(name, DELETING_DATA);
	}

	getJustData = (content) => dlv(content, 'result', 'No Data');

	getDownloadData = () => {
		const rows = [
			['Name', 'Source', 'Description', 'Value'],
			...Object.keys(this.state.userData).reduce((acc, k) => {
				let obj = this.state.userData[k];
				//ignore deleted fields for the download
				if (obj.status === HAS_DATA) {
					acc.push([obj.name||k, obj.source, obj.description, JSON.stringify(obj.value)]);
				}
				return acc;
			}, [])
		];

		let csvContent = 'data:text/tsv;charset=utf-8,'
			+ rows.map(e => e.join('\t')).join('\n');

		return encodeURI(csvContent);
	}

	getStatus = (type) => {
		let status = this.state.userData[type].status;
		return status === HAS_DATA ?
			(
				<Button class={style.delete} type="primary" brand="primary" onClick={callWith(this.deleteData, type)} >
					<Text id="statusDeleteButton" />
				</Button>
			) :
			<Text id={STATUS_TO_TEXT_ID[this.state.userData[type].status]} />;
	}

	onDownloadClick = () => {
		trackEvent( this.context, {
			[INPUT_KEYS.action]: 'click',
			[INPUT_KEYS.priority]: 'high',
			[STANDARD_KEYS.cubecomponent]: 'CcpaData.Download'
		});
	}

	componentWillMount() {
		//Get personal data from cookies
		let cookieMap = (document.cookie || '').split(';').reduce((cm, kv) => {
			let [ name, value ] = kv.split('=');
			cm[name.trim()] = value;
			return cm;
		}, {});

		let userData = {};

		//Add data for present personal cookies and localstorage
		[
			[PERSONAL_COOKIES, TYPE_COOKIE, 'cookie'],
			[PERSONAL_LOCAL_STORAGE, TYPE_LOCALSTORAGE, 'local storage']
		].forEach(([metaArr,storageType, storageStr]) => {
			metaArr.forEach(([name, description, formatterFn]) => {
				let value = storageType === TYPE_COOKIE ? cookieMap[name] : localStorage.getItem(name);
				let keyExists = storageType === TYPE_COOKIE ? name in cookieMap : value !== null;
				if (!keyExists) return; //skip missing entries
				userData[name] = {
					name: `${name} (${storageStr})`,
					description,
					type: storageType,
					source: 'User Interaction',
					status: HAS_DATA,
					value: formatterFn ? formatterFn(value) : value
				};
			});
		});

		this.setState({ userData });
	}

	renderOriginalVersion({ buttonLinks, intlFields, mockDataInjectionComponent, authenticated, userData }) {
		return (
			<div class={style.ccpa}>
				<div class={style.title}><Text id="dataTitle" /></div>
				<div class={style.description}><Text id="dataDescription1" fields={intlFields} /></div>
				<div class={style.description}><Text id="dataDescription2" fields={intlFields} /></div>
				<div class={style.description}><MarkupText id="dataDescription3" fields={intlFields} /></div>
				<div class={style.sectionTitle}><Text id="dataSoldTitle" /></div>
				<div class={style.sectionDescription}><MarkupText id="dataSoldDescription1" fields={intlFields} /></div>
				<div class={style.sectionDescription}><MarkupText id="dataSoldDescription2" fields={intlFields} /></div>

				{this.renderUserDataSection({ mockDataInjectionComponent, authenticated, userData })}

				<div class={style.sectionTitle}><Text id="linksTitle" /></div>
				<CcpaButtons links={buttonLinks} />
			</div>);
	}

	render20201123Version({ buttonLinks, clientCCPALink, intlFields, mockDataInjectionComponent, authenticated, userData, foo }) {
		return (
			<div class={style.ccpa}>
				<div class={style.title}><Text id="20201123.dataTitle" /></div>
				<div class={style.description}><Text id="20201123.dataDescription1" fields={intlFields} /><MarkupText id={`20201123.descriptionCoveredInteractionsWith${dlv(clientCCPALink, 'url') ? '' : 'out'}CCPAResource`} fields={intlFields} /></div>
				<div class={style.description}><MarkupText id="20201123.dataDescription2" fields={intlFields} /></div>
				<div class={style.description}><MarkupText id="20201123.dataDescription3" fields={intlFields} /></div>
				<div class={style.sectionTitle}><Text id="20201123.dataSoldTitle" /></div>
				<div class={style.sectionDescription}><MarkupText id="20201123.dataSoldDescription1" fields={intlFields} /></div>
				<div class={style.sectionDescription}><MarkupText id="20201123.dataSoldDescription2" fields={intlFields} /></div>

				{this.renderUserDataSection({ mockDataInjectionComponent, authenticated, userData })}

				<div class={style.sectionTitle}><Text id="linksTitle" /></div>
				<CcpaButtons links={buttonLinks} />
			</div>);
	}

	renderUserDataSection({ mockDataInjectionComponent, authenticated, userData }) {
		return (<div class={style.data}>
			<div class={style.sectionTitle}><Text id="dataUserTitle" /></div>
			{ mockDataInjectionComponent }
			{ !authenticated && <div class={style.description}><Text id="loginToSeeData" /></div> }
			<div class={style.description}><Text id="dataUserDescription" /></div>
			<div class={style.tableContainer}>
				<table>
					<tr>
						<th>Name</th>
						<th>Source</th>
						<th>Status</th>
						<th>Description</th>
						<th>Value</th>
					</tr>
					{
						Object.keys(userData).map((k) =>
							(<tr>
								<td>{userData[k].name || k}</td>
								<td>{userData[k].source}</td>
								<td  class={cx([DELETED_DATA, DELETE_FAILED].includes(userData[k].status) && style.deleted)}>{this.getStatus(k)}</td>
								<td>{userData[k].description}</td>
								<td><pre>{JSON.stringify(userData[k].value, null, 2)}</pre></td>
							</tr>)
						)
					}
				</table>
			</div>
			<a href={this.getDownloadData()} download="my_data.tsv">
				<Button class={style.download} type="primary" brand="primary" onClick={callWith(this.onDownloadClick)}>
					<Text id="downloadButton" />
				</Button>
			</a>
		</div>);
	}

	render({ links, clientCCPALink, clientDisplayName, mockDataInjectionComponent, overrideVersion }, { authenticated, userData }) {
		let buttonLinks = [links.main, links.partners, links.privacy],
			hostname = window.location.hostname;
		let intlFields = { hostname, ccpaLinks: { ...links, clientCCPA: clientCCPALink }, clientDisplayName };

		return overrideVersion === '20201123' ?
			this.render20201123Version({ buttonLinks, intlFields, clientCCPALink, mockDataInjectionComponent, authenticated, userData }) :
			this.renderOriginalVersion({ buttonLinks, intlFields, mockDataInjectionComponent, authenticated, userData });
	}
}

export const CcpaData = (configure({
	overrideVersion: 'privacyWidgetsConfig.overrideVersion',
	links: 'privacyWidgetsConfig.ccpa.links',
	clientDisplayName: 'clientDisplayName',
	clientCCPALink: 'privacyWidgetsConfig.ccpa.links.clientCCPA' //break out separately for easier toggling/mocking of it in DEMO
})((props) =>  (
	<CcpaDataWidget {...props} />
)));



// WEBPACK FOOTER //
// ../node_modules/@cube/privacy-widgets/src/components/ccpa-data/index.js